// Request detail: bids, negotiation, completion, ratings.
import React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Star, ShieldCheck, CheckCircle2, Send, X, AlertTriangle, MessageSquare, Calculator } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChatSocket } from "@/hooks/useChatSocket";
import { useUnreadThreads } from "@/hooks/useUnreadThreads";
import RouteMap from "@/components/RouteMap";
import { easing, StatusPill, VehicleIcon, CountdownChip, AvailabilityBadge, UnreadDot, BidStatusPill, F, TextInput, DatePicker } from "./shared";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BidThreadModal } from "./BidThreadModal";

// ---------- Request Detail: bids + chat + accept + rate ----------

// Photos stream through an authed endpoint — fetch as blob so <img> works
function PhotoThumb({ reqId, photo }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let url = null;
    api
      .get(`/requests/${reqId}/photos/${photo.id}/file`, { responseType: "blob" })
      .then((r) => {
        if (alive) {
          url = URL.createObjectURL(r.data);
          setSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [reqId, photo.id]);
  if (!src) {
    return <div className="w-20 h-20 bg-[#141416] border border-white/[0.08] animate-pulse" data-testid={`photo-loading-${photo.id}`} />;
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" data-testid={`photo-${photo.phase}-${photo.id}`} title={`${photo.phase} photo`}>
      <img src={src} alt={`${photo.phase} proof`} className="w-20 h-20 object-cover border border-white/[0.12]" />
    </a>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidForm, setBidForm] = useState({ price_usd: "", message: "" });
  const [searchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false); // shipper edit modal
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // Power Profit's "Make the bid" button links back here with ?quote=<payout> —
  // prefill the bid amount so the driver reviews and taps submit (never auto-bid).
  useEffect(() => {
    const q = parseFloat(searchParams.get("quote"));
    if (Number.isFinite(q) && q > 0 && q <= 1000000) {
      setBidForm((f) => (f.price_usd ? f : { ...f, price_usd: String(Math.round(q)) }));
    }
  }, [searchParams]);
  const [chatText, setChatText] = useState("");
  const [ratingForm, setRatingForm] = useState({ score: 5, comment: "" });
  const [hasRated, setHasRated] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [etaBusy, setEtaBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const pickupFileRef = useRef(null);
  const deliveryFileRef = useRef(null);

  const load = async () => {
    try {
      const [r, b] = await Promise.all([
        api.get(`/requests/${id}`),
        api.get(`/requests/${id}/bids`),
      ]);
      setReq(r.data);
      setBids(b.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Driver's own profile (for blackout-overlap warning on the bid form)
  useEffect(() => {
    if (user?.role === "driver") {
      api.get(`/drivers/${user.id}`).then((r) => setMyProfile(r.data)).catch(() => {});
    }
  }, [user?.role, user?.id]);

  const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const blackoutOverlap =
    req?.haul_start && req?.haul_end
      ? (myProfile?.blackout_ranges || []).find(
          (r) => r.start <= req.haul_end && req.haul_start <= r.end
        ) || null
      : null;

  const isParticipant =
    user && req &&
    (user.id === req.shipper_id || user.id === req.driver_id || user.role === "admin");
  const chatOpen = isParticipant && req && (req.status === "assigned" || req.status === "completed");

  // WebSocket chat — only wire when chat is open
  const { messages, connected, typingUsers, sendMessage, sendTyping } = useChatSocket(
    chatOpen ? id : null
  );

  const openCalculator = async () => {
    try {
      const miles = req?.distance_miles ? Math.round(req.distance_miles) : 0;
      const payout = parseFloat(bidForm.price_usd) || req?.budget_usd || 0;
      const { data } = await api.post(`/auth/sso/profit-calculator?miles=${miles}&payout=${payout}&haul_id=${id}`);
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't open the profit calculator."));
    }
  };

  const submitBid = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/requests/${id}/bids`, {
        price_usd: parseInt(bidForm.price_usd, 10),
        message: bidForm.message,
      });
      toast.success("Bid submitted.");
      setBidForm({ price_usd: "", message: "" });
      load();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Free bids used — upgrade to Driver Pro.");
        nav("/app/billing");
        return;
      }
      toast.error(apiErrorMessage(err));
    }
  };

  const openEdit = () => {
    setEditForm({
      title: req.title || "",
      vehicle_type: req.vehicle_type || "boat",
      description: req.description || "",
      dimensions: req.dimensions || "",
      weight_lbs: req.weight_lbs ?? "",
      hitch_type: req.hitch_type || "",
      pickup_city: req.pickup_city || "",
      delivery_city: req.delivery_city || "",
      photo_url: req.photo_url || "",
      budget_usd: req.budget_usd ?? "",
      haul_start: req.haul_start || "",
      haul_end: req.haul_end || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editSaving) return;
    setEditSaving(true);
    try {
      // send every field so cleared optionals (budget/weight/dates…) persist as null
      const payload = {
        ...editForm,
        weight_lbs: editForm.weight_lbs === "" ? null : parseInt(editForm.weight_lbs, 10),
        budget_usd: editForm.budget_usd === "" ? null : parseInt(editForm.budget_usd, 10),
        haul_start: editForm.haul_start || null,
        haul_end: editForm.haul_end || null,
      };
      await api.patch(`/requests/${id}`, payload);
      toast.success("Request updated.");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't save the changes."));
    } finally {
      setEditSaving(false);
    }
  };

  const withdrawRequest = async () => {
    if (withdrawing) return;
    if (!window.confirm("Withdraw this request? It disappears from active listings, and every driver who bid gets a text and an email.")) return;
    setWithdrawing(true);
    try {
      const { data } = await api.post(`/requests/${id}/withdraw`);
      toast.success(`Request withdrawn — ${data.drivers_notified} bidding driver${data.drivers_notified === 1 ? "" : "s"} notified.`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't withdraw the request."));
    } finally {
      setWithdrawing(false);
    }
  };

  const acceptBid = async (bidId) => {
    if (!window.confirm("Accept this bid? Other bids will be rejected.")) return;
    try {
      await api.post(`/bids/${bidId}/accept`);
      toast.success("Bid accepted. Chat is now open.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const completeJob = async () => {
    if (!window.confirm("Mark this haul complete?")) return;
    try {
      await api.post(`/requests/${id}/complete`);
      toast.success("Haul completed. Time to leave a rating.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const markPickedUp = async () => {
    if (!window.confirm("Mark this haul picked up? The shipper gets a text.")) return;
    try {
      await api.post(`/requests/${id}/picked-up`);
      toast.success("Marked picked up — the shipper was notified.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const sendEta = async (preset) => {
    if (etaBusy) return;
    setEtaBusy(true);
    try {
      await api.post(`/requests/${id}/eta-text`, { preset });
      toast.success("Update texted to the shipper.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setEtaBusy(false);
    }
  };

  const uploadPhoto = async (file, phase) => {
    if (!file || photoBusy) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("phase", phase);
      fd.append("photo", file);
      await api.post(`/requests/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${phase === "pickup" ? "Pickup" : "Delivery"} photo added — the shipper was texted and emailed.`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setPhotoBusy(false);
    }
  };

  const relist = async () => {
    try {
      await api.post(`/requests/${id}/relist`);
      toast.success("Relisted — fresh 72-hour bid window. Drivers are being pinged.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const withdrawBid = async (bidId) => {
    if (!window.confirm("Withdraw this bid?")) return;
    try {
      await api.post(`/bids/${bidId}/withdraw`);
      toast.success("Bid withdrawn.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const [threadBid, setThreadBid] = useState(null);
  const { by_bid: unreadByBid } = useUnreadThreads();

  const sendChatMsg = (e) => {
    e.preventDefault();
    if (!chatText.trim()) return;
    const ok = sendMessage(chatText);
    if (ok) {
      setChatText("");
      sendTyping(false);
    } else {
      toast.error("Chat is not connected yet — try again in a moment.");
    }
  };

  const onChatChange = (v) => {
    setChatText(v);
    if (v.length > 0) sendTyping(true);
    else sendTyping(false);
  };

  const submitRating = async (e) => {
    e.preventDefault();
    if (!req) return;
    const toUserId = user.id === req.shipper_id ? req.driver_id : req.shipper_id;
    if (!toUserId) return;
    try {
      await api.post("/ratings", {
        to_user_id: toUserId,
        request_id: id,
        score: parseInt(ratingForm.score, 10),
        comment: ratingForm.comment,
      });
      toast.success("Rating submitted. Thanks.");
      setHasRated(true);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  if (loading || !req) {
    return (
      <div className="px-6 md:px-10 py-14 max-w-4xl mx-auto text-zinc-500 uppercase text-xs tracking-widest animate-pulse">
        Loading haul…
      </div>
    );
  }

  const isShipper = user?.id === req.shipper_id;
  const isDriver = user?.role === "driver";
  const isAcceptedDriver = user?.id === req.driver_id;
  const canBid = isDriver && req.status === "open" && !isShipper;
  const canRate = isParticipant && req.status === "completed" && !hasRated;

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-4xl mx-auto space-y-8" data-testid="request-detail">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: easing }}
      >
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <StatusPill status={req.status} />
          <CountdownChip deadline={req.bid_deadline} status={req.status} testid="detail-countdown" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
            Posted {new Date(req.created_at).toLocaleDateString()}
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-tight">{req.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-zinc-400">
          <span className="flex items-center gap-2 font-semibold text-white">
            <MapPin className="w-4 h-4 text-[#FF3B30]" /> {req.pickup_city}
            <ArrowRight className="w-3 h-3 text-zinc-600" />
            {req.delivery_city}
          </span>
        </div>
      </motion.div>

      {req.status === "expired" && (
        <div
          className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-4 flex items-center justify-between gap-4 flex-wrap"
          data-testid="expired-banner"
        >
          <div className="text-sm text-zinc-300">
            <span className="text-[#FF3B30] font-bold uppercase tracking-widest text-xs block mb-1">Bidding closed</span>
            The 72-hour window ended without an accepted bid.
          </div>
          {isShipper && (
            <button
              onClick={relist}
              data-testid="relist-btn"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] py-2.5 px-4 transition-all"
            >
              Relist — new 72h window
            </button>
          )}
        </div>
      )}

      {req.pickup_lat != null && req.delivery_lat != null && (
        <RouteMap
          pickup={{ lat: req.pickup_lat, lng: req.pickup_lng }}
          delivery={{ lat: req.delivery_lat, lng: req.delivery_lng }}
          height={220}
        />
      )}
      {req.distance_miles != null && (
        <div className="flex items-center gap-4 text-sm">
          <div className="border border-white/[0.08] bg-[#0F0F10] px-4 py-2 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Distance</span>
            <span className="font-display text-xl font-black">{req.distance_miles.toFixed(0)} mi</span>
          </div>
          {req.duration_min != null && (
            <div className="border border-white/[0.08] bg-[#0F0F10] px-4 py-2 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Drive</span>
              <span className="font-display text-xl font-black">
                {Math.floor(req.duration_min / 60)}h {req.duration_min % 60}m
              </span>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCell label="Vehicle" value={req.vehicle_type} icon={VehicleIcon} vehicle={req.vehicle_type} />
        <InfoCell label="Dimensions" value={req.dimensions || "—"} />
        <InfoCell label="Weight" value={req.weight_lbs ? `${req.weight_lbs} lbs` : "—"} />
        <InfoCell label="Hitch" value={req.hitch_type || "—"} />
        <InfoCell label="Budget" value={req.budget_usd ? `$${req.budget_usd.toLocaleString()}` : "Open"} />
        {req.haul_start && req.haul_end && (
          <InfoCell label="Haul window" value={`${fmtDay(req.haul_start)} – ${fmtDay(req.haul_end)}`} />
        )}
        {req.picked_up_at && (
          <div data-testid="picked-up-chip">
            <InfoCell label="Picked up" value={fmtDay(String(req.picked_up_at).slice(0, 10))} />
          </div>
        )}
        <div className="border border-white/[0.08] bg-[#0F0F10] p-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Shipper</div>
          <div className="mt-1.5 text-white font-semibold flex items-center gap-2">
            {req.shipper_name}
            {req.shipper_verified && (
              <span
                data-testid="shipper-verified-detail"
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black bg-emerald-500 text-black px-1.5 py-0.5 normal-case"
              >
                <ShieldCheck className="w-2.5 h-2.5" /> Verified
              </span>
            )}
          </div>
        </div>
        {req.shipper_rating_count != null && (
          <InfoCell
            label="Shipper rep"
            value={(() => {
              const n = req.shipper_completed_jobs ?? 0;
              const hauls = `${n} ${n === 1 ? "haul" : "hauls"}`;
              return req.shipper_rating_count > 0
                ? `${req.shipper_rating_avg.toFixed(1)}/5 (${req.shipper_rating_count}) · ${hauls}`
                : `New shipper · ${hauls}`;
            })()}
          />
        )}
      </div>

      <section>
        <h2 className="font-display text-2xl font-black uppercase mb-3">Description</h2>
        <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{req.description}</p>
      </section>

      {/* BIDS */}
      <section data-testid="bids-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-black uppercase">
            Bids <span className="text-[#FF3B30]">({bids.length})</span>
          </h2>
          {isShipper && req.status === "assigned" && (
            <button
              onClick={completeJob}
              data-testid="complete-btn"
              className="text-xs uppercase tracking-widest font-bold border-2 border-emerald-500/70 hover:bg-emerald-500 hover:text-black text-emerald-400 px-4 py-2.5 transition-all"
            >
              Mark complete
            </button>
          )}
          {isDriver && req.status === "assigned" && req.driver_id === user?.id && !req.picked_up_at && (
            <button
              onClick={markPickedUp}
              data-testid="picked-up-btn"
              className="text-xs uppercase tracking-widest font-bold border-2 border-amber-500/70 hover:bg-amber-500 hover:text-black text-amber-400 px-4 py-2.5 transition-all"
            >
              Mark picked up
            </button>
          )}
        </div>

        {isDriver && req.status === "assigned" && req.driver_id === user?.id && (
          <div className="mb-4 border border-white/[0.08] bg-[#0F0F10] p-4" data-testid="eta-text-section">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Text the shipper an update</div>
            <div className="flex flex-wrap gap-2">
              {[
                ["running_late", "Running late"],
                ["arriving_today", "Arriving today"],
                ["arriving_tomorrow", "Arriving tomorrow"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => sendEta(k)}
                  disabled={etaBusy}
                  data-testid={`eta-${k}`}
                  className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.15] text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors disabled:opacity-50"
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-600 mt-2">One text per haul per hour. Goes straight to the shipper's phone.</p>
          </div>
        )}

        {isParticipant && (req.status === "assigned" || (req.photos || []).length > 0) && (
          <div className="mb-4 border border-white/[0.08] bg-[#0F0F10] p-4" data-testid="photo-proof-section">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Photo proof</div>
            {isDriver && req.status === "assigned" && req.driver_id === user?.id && (
              <div className="flex flex-wrap gap-2 mb-3">
                <input
                  ref={pickupFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-pickup-input"
                  onChange={(e) => { uploadPhoto(e.target.files[0], "pickup"); e.target.value = ""; }}
                />
                <input
                  ref={deliveryFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-delivery-input"
                  onChange={(e) => { uploadPhoto(e.target.files[0], "delivery"); e.target.value = ""; }}
                />
                <button
                  onClick={() => pickupFileRef.current?.click()}
                  disabled={photoBusy}
                  data-testid="add-pickup-photo"
                  className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.15] text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors disabled:opacity-50"
                >
                  {photoBusy ? "Uploading…" : "Add pickup photo"}
                </button>
                <button
                  onClick={() => deliveryFileRef.current?.click()}
                  disabled={photoBusy}
                  data-testid="add-delivery-photo"
                  className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.15] text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors disabled:opacity-50"
                >
                  Add delivery photo
                </button>
              </div>
            )}
            {(req.photos || []).length === 0 ? (
              <p className="text-[11px] text-zinc-600" data-testid="photo-empty">No photos yet — condition shots at pickup and dropoff protect everyone.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="photo-gallery">
                {(req.photos || []).map((p) => (
                  <PhotoThumb key={p.id} reqId={id} photo={p} />
                ))}
              </div>
            )}
          </div>
        )}

        {isShipper && req.status === "open" && (
          <div className="border border-white/[0.08] bg-[#0F0F10] p-5 mb-5 flex items-center justify-between gap-4 flex-wrap" data-testid="shipper-actions">
            <p className="text-[11px] text-zinc-500 uppercase tracking-widest">
              Open for bids — you can still change the details or pull it down.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={openEdit}
                data-testid="edit-request-btn"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.15] text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
              >
                Edit
              </button>
              <button
                onClick={withdrawRequest}
                disabled={withdrawing}
                data-testid="withdraw-request-btn"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold px-4 py-2.5 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
              >
                {withdrawing ? "Withdrawing…" : "Withdraw"}
              </button>
            </div>
          </div>
        )}
        {isShipper && req.status === "assigned" && (
          <div
            className="border border-amber-500/40 bg-amber-500/[0.07] p-4 mb-5 text-xs text-amber-300 flex items-start gap-2"
            data-testid="request-locked-note"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              This load is in progress — a driver has accepted it, so editing and withdrawal are locked.
              Coordinate in the moderated inbox below.
            </span>
          </div>
        )}

        {canBid && (
          <form onSubmit={submitBid} className="border border-white/[0.08] bg-[#0F0F10] p-5 mb-5 space-y-3" data-testid="bid-form">
            {blackoutOverlap && (
              <div
                className="border border-amber-500/40 bg-amber-500/[0.07] p-3 text-xs text-amber-300 flex items-start gap-2"
                data-testid="blackout-overlap-warning"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Heads up: this haul window ({fmtDay(req.haul_start)} – {fmtDay(req.haul_end)}) overlaps your
                  blackout {fmtDay(blackoutOverlap.start)} – {fmtDay(blackoutOverlap.end)}. You can still bid,
                  but the shipper sees your blackout dates.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-1.5">Bid (USD)</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={bidForm.price_usd}
                  onChange={(e) => setBidForm((f) => ({ ...f, price_usd: e.target.value }))}
                  placeholder="1200"
                  data-testid="bid-price-input"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-1.5">Message</label>
                <input
                  type="text"
                  required
                  minLength={5}
                  value={bidForm.message}
                  onChange={(e) => setBidForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Available this weekend, insured, F-350 dually…"
                  data-testid="bid-message-input"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="submit"
                data-testid="submit-bid"
                className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] py-3 px-5 transition-all"
              >
                Submit bid <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={openCalculator}
                data-testid="calc-profit-link"
                title="Open the profit calculator prefilled with this haul's miles and your bid"
                className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 hover:border-[#FF3B30] hover:text-[#FF3B30] font-bold uppercase tracking-widest text-xs py-3 px-5 transition-all"
              >
                <Calculator className="w-4 h-4" /> Calculate profit
              </button>
            </div>
          </form>
        )}

        {bids.length === 0 ? (
          <div className="border border-dashed border-white/[0.08] p-8 text-center text-zinc-500 text-sm uppercase tracking-widest">
            No bids yet
          </div>
        ) : (
          <div className="space-y-3">
            {bids.map((b) => (
              <div
                key={b.id}
                data-testid={`bid-${b.id}`}
                className={`border ${b.driver_pro ? "border-[#FF3B30]/60 bg-gradient-to-br from-[#FF3B30]/[0.05] to-[#0F0F10]" : "border-white/[0.08] bg-[#0F0F10]"} p-5 flex items-start justify-between gap-4`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Link
                      to={`/drivers/${b.driver_id}`}
                      className="font-display text-lg font-black uppercase hover:text-[#FF3B30] transition-colors"
                    >
                      {b.driver_name}
                    </Link>
                    {b.driver_pro && (
                      <span
                        data-testid={`pro-badge-${b.id}`}
                        className="inline-flex items-center gap-1 bg-[#FF3B30] text-white text-[10px] uppercase tracking-widest font-black px-2 py-0.5"
                        title="Driver Pro subscriber"
                      >
                        <Star className="w-2.5 h-2.5" fill="#fff" strokeWidth={0} /> Pro
                      </span>
                    )}
                    <AvailabilityBadge
                      status={b.driver_availability}
                      blackoutUntil={b.driver_blackout_until}
                      testid={`bid-availability-${b.id}`}
                    />
                    <BidStatusPill status={b.status} />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                      {new Date(b.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-300 mt-2">{b.message}</p>
                  {(isShipper || user?.id === b.driver_id) && (
                    <button
                      onClick={() => setThreadBid(b)}
                      data-testid={`open-thread-${b.id}`}
                      className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-300 hover:text-[#FF3B30] border border-white/10 hover:border-[#FF3B30]/60 px-3 py-1.5 transition-all"
                    >
                      <MessageSquare className="w-3.5 h-3.5" /> Negotiate
                      <UnreadDot count={unreadByBid[b.id]} testid={`unread-badge-${b.id}`} />
                    </button>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl font-black uppercase">${b.price_usd.toLocaleString()}</div>
                  {isShipper && req.status === "open" && b.status === "pending" && (
                    <button
                      onClick={() => acceptBid(b.id)}
                      data-testid={`accept-bid-${b.id}`}
                      className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white border border-[#FF3B30] hover:bg-[#FF3B30] px-3 py-1.5 transition-all"
                    >
                      Accept <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {user?.id === b.driver_id && b.status === "pending" && (
                    <button
                      onClick={() => withdrawBid(b.id)}
                      data-testid={`withdraw-bid-${b.id}`}
                      className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-[#FF3B30] border border-white/10 hover:border-[#FF3B30]/60 px-3 py-1.5 transition-all"
                    >
                      Withdraw <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CHAT — only after assignment, for participants (WebSocket) */}
      {chatOpen && (
        <section data-testid="chat-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl font-black uppercase">
              Moderated inbox
            </h2>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
              <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} />
              <span className="text-zinc-500">{connected ? "Live" : "Connecting…"}</span>
            </div>
          </div>
          <div className="border border-white/[0.08] bg-[#0F0F10] p-4 flex flex-col gap-3 max-h-[420px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-zinc-500 text-sm py-6 text-center">
                No messages yet — say hi.
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 ${mine ? "bg-[#FF3B30] text-white" : "bg-[#1A1A1C] text-white"} ${m.flagged ? "ring-2 ring-red-500/50" : ""}`}>
                      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{m.sender_name}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                      {(m.moderated || m.flagged) && (
                        <div className="text-[10px] uppercase tracking-widest mt-1.5 opacity-80 flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" />
                          {m.flagged ? `Flagged: ${m.flag_reason || "policy"}` : "Redacted"}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {Object.keys(typingUsers).length > 0 && (
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 italic pl-1" data-testid="typing-indicator">
                {Object.values(typingUsers).join(", ")} typing<span className="inline-block animate-pulse">…</span>
              </div>
            )}
          </div>
          <form onSubmit={sendChatMsg} className="mt-3 flex gap-2" data-testid="chat-form">
            <input
              value={chatText}
              onChange={(e) => onChatChange(e.target.value)}
              onBlur={() => sendTyping(false)}
              placeholder="Type a message. PII gets auto-redacted; scams get flagged."
              data-testid="chat-input"
              className="flex-1 bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3"
            />
            <button
              type="submit"
              disabled={!connected}
              data-testid="chat-send"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] px-4"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </section>
      )}

      {/* RATING — after complete, for participants */}
      {canRate && (
        <section className="border border-white/[0.08] bg-[#0F0F10] p-5" data-testid="rating-section">
          <h2 className="font-display text-2xl font-black uppercase mb-3">Rate this haul</h2>
          <form onSubmit={submitRating} className="space-y-3">
            <div className="flex items-center gap-2" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRatingForm((f) => ({ ...f, score: n }))}
                  className={`p-1.5 border transition-colors ${
                    n <= ratingForm.score ? "border-[#FF3B30] text-[#FF3B30]" : "border-white/10 text-zinc-600 hover:text-white"
                  }`}
                  aria-label={`${n} star`}
                  data-testid={`rating-${n}`}
                >
                  <Star className="w-5 h-5" fill={n <= ratingForm.score ? "#FF3B30" : "none"} />
                </button>
              ))}
            </div>
            <textarea
              value={ratingForm.comment}
              onChange={(e) => setRatingForm((f) => ({ ...f, comment: e.target.value }))}
              placeholder="Optional comment"
              rows={3}
              data-testid="rating-comment"
              className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-3"
            />
            <button
              type="submit"
              data-testid="submit-rating"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] hover:border-[#D62828] py-3 px-5 transition-all"
            >
              Submit rating <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </section>
      )}
      {threadBid && (
        <BidThreadModal
          bid={threadBid}
          onClose={() => setThreadBid(null)}
          onChange={load}
        />
      )}

      {/* Shipper edit modal — open requests only (locked once a driver accepts) */}
      <Dialog open={editOpen} onOpenChange={(open) => { if (!editSaving) setEditOpen(open); }}>
        <DialogContent className="bg-[#0F0F10] border-white/[0.12] text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black uppercase">Edit request</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Changes are visible to drivers immediately. Editing locks once a driver accepts the load.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="edit-request-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Vehicle type">
                  <select
                    value={editForm.vehicle_type}
                    onChange={(e) => setEditForm((f) => ({ ...f, vehicle_type: e.target.value }))}
                    data-testid="edit-input-vehicle-type"
                    className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white p-4"
                  >
                    <option value="boat">Boat</option>
                    <option value="rv">RV / Fifth-wheel</option>
                    <option value="trailer">Trailer</option>
                    <option value="other">Other</option>
                  </select>
                </F>
                <TextInput label="Title" name="title" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} placeholder="24' Wakeboat, Boise → Coeur d'Alene" testid="edit-input-title" required />
              </div>
              <F label="Description">
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  data-testid="edit-input-description"
                  required
                  rows={4}
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4"
                />
              </F>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Pickup city" name="pickup_city" value={editForm.pickup_city} onChange={(e) => setEditForm((f) => ({ ...f, pickup_city: e.target.value }))} placeholder="Boise, ID" testid="edit-input-pickup" required />
                <TextInput label="Delivery city" name="delivery_city" value={editForm.delivery_city} onChange={(e) => setEditForm((f) => ({ ...f, delivery_city: e.target.value }))} placeholder="Coeur d'Alene, ID" testid="edit-input-delivery" required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextInput label="Dimensions (L x W x H)" name="dimensions" value={editForm.dimensions} onChange={(e) => setEditForm((f) => ({ ...f, dimensions: e.target.value }))} placeholder="24' x 8.5' x 9'" testid="edit-input-dimensions" />
                <TextInput label="Weight (lbs)" name="weight_lbs" type="number" value={editForm.weight_lbs} onChange={(e) => setEditForm((f) => ({ ...f, weight_lbs: e.target.value }))} placeholder="7500" testid="edit-input-weight" />
                <TextInput label="Hitch type" name="hitch_type" value={editForm.hitch_type} onChange={(e) => setEditForm((f) => ({ ...f, hitch_type: e.target.value }))} placeholder="Class IV, 2-5/16 ball" testid="edit-input-hitch" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Photo URL (optional)" name="photo_url" value={editForm.photo_url} onChange={(e) => setEditForm((f) => ({ ...f, photo_url: e.target.value }))} placeholder="https://…" testid="edit-input-photo" />
                <TextInput label="Budget (USD, optional)" name="budget_usd" type="number" value={editForm.budget_usd} onChange={(e) => setEditForm((f) => ({ ...f, budget_usd: e.target.value }))} placeholder="1200" testid="edit-input-budget" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Haul window start (optional)">
                  <DatePicker value={editForm.haul_start} onChange={(v) => setEditForm((f) => ({ ...f, haul_start: v }))} testid="edit-input-haul-start" />
                </F>
                <F label="Haul window end (optional)">
                  <DatePicker value={editForm.haul_end} onChange={(v) => setEditForm((f) => ({ ...f, haul_end: v }))} testid="edit-input-haul-end" />
                </F>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  disabled={editSaving}
                  data-testid="edit-cancel-btn"
                  className="text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border border-white/[0.12] text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  data-testid="edit-save-btn"
                  className="text-[11px] uppercase tracking-widest font-bold px-5 py-2.5 bg-[#FF3B30] text-white hover:bg-[#ff564d] transition-colors disabled:opacity-40"
                >
                  {editSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function InfoCell({ label, value, vehicle }) {
  return (
    <div className="border border-white/[0.08] bg-[#0F0F10] p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{label}</div>
      <div className="mt-1.5 text-white font-semibold flex items-center gap-2 capitalize">
        {vehicle && <VehicleIcon type={vehicle} />}
        {value}
      </div>
    </div>
  );
}

